import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    referral_source: "",
    message: "",
    consent: true,
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ state: "error", msg: "Please accept the consent to continue." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        msg: "Thank you. We'll reach out shortly to schedule your free consultation.",
      });
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        referral_source: "",
        message: "",
        consent: true,
      });
    } catch (err) {
      setStatus({
        state: "error",
        msg: err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0E0E11] text-white py-24 lg:py-32 overflow-hidden border-y border-white/[0.04]"
    >
      <div
        className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#F5A623]/[0.08] blur-3xl"
        aria-hidden
      />
      <div
        className="absolute top-1/4 -right-40 w-[400px] h-[400px] rounded-full bg-[#F5A623]/[0.05] blur-3xl"
        aria-hidden
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start relative">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-semibold">
            <span className="divider-gold" /> Book a Free Consultation
          </span>
          <h2
            data-testid="contact-headline"
            className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight silver-text"
          >
            Let's see how we can make a positive difference in <em className="text-[#F5A623]">your</em> life.
          </h2>
          <p className="mt-5 text-[#B7BBC4] leading-relaxed max-w-md">
            We're looking forward to meeting you. Share a few details and we'll arrange a no-pressure conversation about your goals.
          </p>

          <div className="mt-12 space-y-5 text-sm">
            <a href={`mailto:${SITE.contact.email}`} className="flex items-center gap-3 text-[#ECECEE] hover:text-[#F5A623] transition-colors" data-testid="contact-email">
              <span className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center">
                <Mail className="w-4 h-4 text-[#F5A623]" />
              </span>
              {SITE.contact.email}
            </a>
            <a href={`tel:${SITE.contact.phone}`} className="flex items-center gap-3 text-[#ECECEE] hover:text-[#F5A623] transition-colors" data-testid="contact-phone">
              <span className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center">
                <Phone className="w-4 h-4 text-[#F5A623]" />
              </span>
              T: {SITE.contact.phone}
            </a>
            <div className="flex items-start gap-3 text-[#ECECEE]" data-testid="contact-address">
              <span className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-[#F5A623]" />
              </span>
              <span className="leading-relaxed mt-2">{SITE.contact.address}</span>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 glass-card rounded-md p-7 lg:p-10"
          data-testid="contact-form"
        >
          <h3 className="font-serif text-2xl text-white">Let's connect</h3>
          <p className="text-sm text-[#9CA0AA] mt-1">
            Fill out the form to begin discussing how we can help you.
          </p>

          <div className="mt-7 grid sm:grid-cols-2 gap-4">
            <Field label="First name" required value={form.first_name} onChange={update("first_name")} testId="contact-first-name" />
            <Field label="Last name" required value={form.last_name} onChange={update("last_name")} testId="contact-last-name" />
            <Field label="Email" type="email" required value={form.email} onChange={update("email")} testId="contact-email-input" />
            <Field label="Phone" required value={form.phone} onChange={update("phone")} testId="contact-phone-input" />
          </div>
          <div className="mt-4">
            <Label>How did you hear about us?</Label>
            <input
              data-testid="contact-referral"
              value={form.referral_source}
              onChange={update("referral_source")}
              className="mt-1.5 w-full rounded-md input-dark px-3.5 py-2.5 text-sm"
            />
          </div>
          <div className="mt-4">
            <Label>What's on your mind? (optional)</Label>
            <textarea
              data-testid="contact-message"
              value={form.message}
              onChange={update("message")}
              rows={4}
              className="mt-1.5 w-full rounded-md input-dark px-3.5 py-2.5 text-sm resize-none"
              placeholder="Briefly tell us about your goals..."
            />
          </div>

          <label className="mt-5 flex items-start gap-3 text-xs text-[#9CA0AA] cursor-pointer">
            <input
              data-testid="contact-consent"
              type="checkbox"
              checked={form.consent}
              onChange={update("consent")}
              className="mt-0.5 accent-[#F5A623]"
            />
            <span>
              I agree to receive electronic communications from Sunrise Wealth Management. I understand I may withdraw my consent at any time.
            </span>
          </label>

          <button
            type="submit"
            disabled={status.state === "loading"}
            data-testid="contact-submit"
            className="mt-6 group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#F5A623] text-[#0A0A0C] text-sm font-medium tracking-wide hover:bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
          >
            {status.state === "loading" ? "Sending..." : "Submit"}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>

          {status.state === "success" && (
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-emerald-400" data-testid="contact-success">
              <CheckCircle2 className="w-4 h-4" /> {status.msg}
            </p>
          )}
          {status.state === "error" && (
            <p className="mt-4 inline-flex items-center gap-2 text-sm text-red-400" data-testid="contact-error">
              <AlertCircle className="w-4 h-4" /> {status.msg}
            </p>
          )}
        </motion.form>
      </div>
    </section>
  );
}

function Label({ children }) {
  return (
    <label className="text-xs uppercase tracking-[0.18em] text-white font-semibold">
      {children}
    </label>
  );
}

function Field({ label, type = "text", required, value, onChange, testId }) {
  return (
    <div>
      <Label>{label}{required && " *"}</Label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="mt-1.5 w-full rounded-md input-dark px-3.5 py-2.5 text-sm"
      />
    </div>
  );
}
